<template>
  <div>
    <region-list-add-new
      :is-add-new-region-sidebar-active.sync="isAddNewRegionSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />

    <region-list-edit
      :is-edit-region-sidebar-active.sync="isEditRegionSidebarActive"
      :region-id="regionId"
      :region-name="regionName"
      :region-code="regionCode"
      :region-city="regionCity"
      :region-address="regionAddress"
      :region-phone="regionPhone"
      :region-fax="regionFax"
    />

    <app-page-shell
      title="Regions"
      subtitle="Manage operational regions and their assigned users."
    >
      <app-list-toolbar
        :per-page="perPage"
        :per-page-options="perPageOptions"
        @update:perPage="perPage = $event"
      >
        <template #search>
          <div class="app-toolbar-actions">
            <b-form-input
              v-model="searchQuery"
              class="app-toolbar-search"
              placeholder="Search..."
            />
            <b-button
              variant="primary"
              @click="isAddNewRegionSidebarActive = true"
            >
              <feather-icon icon="PlusIcon" />
              <span class="align-middle ml-50 text-nowrap">Add Region</span>
            </b-button>
          </div>
        </template>
      </app-list-toolbar>

      <b-table
        ref="refRegionListTable"
        class="position-relative"
        :items="fetchRegions"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 6"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <template #cell(region_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.region_name }}</span>
          </div>
        </template>

        <template #cell(region_code)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.region_code }}</span>
          </div>
        </template>

        <template #cell(region_city)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.region_city }}</span>
          </div>
        </template>

        <template #cell(users)="data">
          <div class="text-nowrap">
            <span
              v-for="user in data.item.users"
              :key="user.user_id"
              class="badge bg-primary rounded-pill mr-50"
            >
              {{ user.user_fullname }}
            </span>
            <span
              v-if="!data.item.users || data.item.users.length === 0"
              class="text-muted"
            >
              -
            </span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editRegion(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteRegion(data.item.region_id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <app-pagination-footer
        :meta="dataMeta"
        :current-page="currentPage"
        :total-rows="totalRegions"
        :per-page="perPage"
        @update:currentPage="currentPage = $event"
      />
    </app-page-shell>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSkeletonTable,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'

import apiEndpoints from '@/constants/apiEndpoints'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import AppListToolbar from '@/views/apps/components/AppListToolbar.vue'
import AppPaginationFooter from '@/views/apps/components/AppPaginationFooter.vue'
import useRegionList from './RegionList'
import regionModule from '../regionModule'
import RegionListAddNew from './RegionAddNew.vue'
import RegionListEdit from './RegionEdit.vue'

export default {
  components: {
    RegionListEdit,
    RegionListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSkeletonTable,
    vSelect,
    AppPageShell,
    AppListToolbar,
    AppPaginationFooter,
  },
  data() {
    return {
      userRole: '',
    }
  },
  setup() {
    const REGION_APP_STORE_MODULE_NAME = 'app-unit'

    // Register module
    if (!store.hasModule(REGION_APP_STORE_MODULE_NAME)) {
      store.registerModule(REGION_APP_STORE_MODULE_NAME, regionModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(REGION_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(REGION_APP_STORE_MODULE_NAME)
      }
    })

    const isAddNewRegionSidebarActive = ref(false)
    const isEditRegionSidebarActive = ref(false)

    const regionId = ref('')
    const regionName = ref('')
    const regionCode = ref('')
    const regionCity = ref('')
    const regionAddress = ref('')
    const regionPhone = ref('')
    const regionFax = ref('')

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchRegions,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalRegions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRegionListTable,
      refetchData,

      resolveRegionRoleVariant,
      resolveRegionRoleIcon,
      resolveRegionStatusVariant,

      roleFilter,
      statusFilter,
      loading,
    } = useRegionList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {
      regionId,
      regionName,
      regionCode,
      regionCity,
      regionAddress,
      regionPhone,
      regionFax,
      isEditRegionSidebarActive,
      isAddNewRegionSidebarActive,
      fetchRegions,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalRegions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRegionListTable,
      refetchData,
      avatarText,
      resolveRegionRoleVariant,
      resolveRegionRoleIcon,
      resolveRegionStatusVariant,
      roleOptions,
      statusOptions,
      roleFilter,
      statusFilter,
      loading,
    }
  },
  mounted() {
    this.userRole = this.$cookies.get('UserRole')
    this.$root.$on('refreshTable', () => {
      if (this.$refs.refRegionListTable) {
        if (this.$refs.refRegionListTable) this.$refs.refRegionListTable.refresh()
      }
    })
  },
  methods: {
    deleteRegion(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_REGION}/${id}`

      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            if (this.$refs.refRegionListTable) this.$refs.refRegionListTable.refresh()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Your data has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.data.message || 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    editRegion(data) {
      this.regionId = data.region_id ? data.region_id.toString() : ''
      this.regionName = data.region_name
      this.regionCode = data.region_code
      this.regionCity = data.region_city
      this.regionAddress = data.region_address
      this.regionPhone = data.region_phone
      this.regionFax = data.region_fax

      this.isEditRegionSidebarActive = true
    },
  },
}
</script>

<style lang="scss" scoped>
.app-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: .75rem;
  min-width: 0;
}

.app-toolbar-search {
  width: 260px;
}

@media (max-width: 991.98px) {
  .app-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .app-toolbar-search {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
